import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Eye, EyeOff } from 'lucide-react';
import { useRateLimit } from '@/hooks/useRateLimit';
import { validatePassword } from '@/lib/security';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useIsMobile } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
} from "@/components/ui/drawer";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { LoginForm } from './auth/LoginForm';
import { SignupForm } from './auth/SignupForm';
import { ResetPasswordRequestForm } from './auth/ResetPasswordRequestForm';
import { ResetPasswordVerifyForm } from './auth/ResetPasswordVerifyForm';
import { OTPVerifyForm } from './auth/OTPVerifyForm';
import { ConfirmationDialog } from '@/components/ui/ConfirmationDialog';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialMode?: 'login' | 'signup' | 'reset' | 'reset-verify';
}



interface AuthContentProps {
  isDrawer?: boolean;
  mode: 'login' | 'signup' | 'reset' | 'reset-verify' | 'otp-verify';
  setMode: (mode: 'login' | 'signup' | 'reset' | 'reset-verify' | 'otp-verify') => void;
  formData: any;
  setFormData: React.Dispatch<React.SetStateAction<any>>;
  loading: boolean;
  isSendingCode: boolean;
  countdown: number;
  resetCountdown: number;
  showPassword: boolean;
  setShowPassword: (show: boolean) => void;
  handleSubmit: (e: React.FormEvent) => void;
  getTitle: () => string;
  getDescription: () => string;
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  sendVerificationCode: () => void;
  onClose: () => void;
  resetPassword: (email: string) => Promise<any>;
}

const AuthContent = ({
  isDrawer = false,
  mode,
  setMode,
  formData,
  setFormData,
  loading,
  isSendingCode,
  countdown,
  resetCountdown,
  showPassword,
  setShowPassword,
  handleSubmit,
  getTitle,
  getDescription,
  handleInputChange,
  sendVerificationCode,
  onClose,
  resetPassword
}: AuthContentProps) => (
  <div className={cn("flex flex-col h-full", isDrawer && "px-4 pb-8")}>
    {!isDrawer && (
      <DialogHeader className="text-center space-y-2 mb-4">
        <DialogTitle className="text-2xl font-bold">
          {getTitle()}
        </DialogTitle>
        <DialogDescription className="text-muted-foreground">
          {getDescription()}
        </DialogDescription>
      </DialogHeader>
    )}

    {/* Toggle Switch */}
    {(mode === 'login' || mode === 'signup') && (
      <div className="flex p-1 bg-muted rounded-lg mb-6 w-full max-w-xs mx-auto">
        <button
          type="button"
          onClick={() => setMode('login')}
          className={cn(
            "flex-1 py-1.5 text-sm font-medium rounded-md transition-all duration-200",
            mode === 'login' ? "bg-background text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
          )}
        >
          התחברות
        </button>
        <button
          type="button"
          onClick={() => setMode('signup')}
          className={cn(
            "flex-1 py-1.5 text-sm font-medium rounded-md transition-all duration-200",
            mode === 'signup' ? "bg-background text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
          )}
        >
          הרשמה
        </button>
      </div>
    )}

    <form onSubmit={handleSubmit} className={cn("space-y-4", isDrawer && "flex-1 overflow-y-auto px-1")}>
      {mode === 'otp-verify' && (
        <OTPVerifyForm
          otpCode={formData.otpCode}
          onOtpChange={(val) => setFormData((prev: any) => ({ ...prev, otpCode: val }))}
          onResendCode={sendVerificationCode}
          countdown={countdown}
          isSendingCode={isSendingCode}
          isDrawer={isDrawer}
        />
      )}

      {mode === 'signup' && (
        <SignupForm
          formData={formData}
          showPassword={showPassword}
          setShowPassword={setShowPassword}
          handleInputChange={handleInputChange}
          isDrawer={isDrawer}
        />
      )}

      {mode === 'login' && (
        <LoginForm
          formData={formData}
          isLoading={loading}
          showPassword={showPassword}
          setShowPassword={setShowPassword}
          handleInputChange={handleInputChange}
          onForgotPassword={() => setMode('reset')}
          isDrawer={isDrawer}
        />
      )}

      {mode === 'reset' && (
        <ResetPasswordRequestForm
          formData={{ email: formData.email }}
          handleInputChange={handleInputChange}
          isDrawer={isDrawer}
        />
      )}

      {mode === 'reset-verify' && (
        <ResetPasswordVerifyForm
          formData={formData}
          showPassword={showPassword}
          setShowPassword={setShowPassword}
          handleInputChange={handleInputChange}
          isDrawer={isDrawer}
        />
      )}

      <div className="pt-4 mt-auto">
        <Button
          type="submit"
          className="w-full h-12 text-base font-medium transition-all"
          disabled={loading || (mode === 'otp-verify' && formData.otpCode.length !== 6)}
        >
          {loading ? (
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 border-2 border-white/50 border-t-white rounded-full animate-spin" />
              <span>
                {mode === 'login' ? 'מתחבר...' : mode === 'signup' ? 'המתן...' : mode === 'otp-verify' ? 'יוצר חשבון...' : mode === 'reset-verify' ? 'מאפס סיסמה...' : 'שולח קוד...'}
              </span>
            </div>
          ) : (
            mode === 'login' ? 'התחבר' : mode === 'signup' ? 'המשך' : mode === 'otp-verify' ? 'צור חשבון' : mode === 'reset-verify' ? 'אפס סיסמה' : 'שלח קוד אימות'
          )}
        </Button>
      </div>

      <div className="text-center space-y-3 pt-4 border-t border-border">
        {mode === 'otp-verify' && (
          <div>
            <Button
              variant="link"
              type="button"
              onClick={() => {
                setMode('signup');
                setFormData((prev: any) => ({ ...prev, otpCode: '' }));
              }}
              className="text-sm text-muted-foreground hover:text-foreground underline-offset-4"
            >
              חזור לעריכת פרטים
            </Button>
          </div>
        )}

        {mode === 'login' && (
          <div>
            <Button
              variant="link"
              size="sm"
              type="button"
              onClick={() => setMode('reset')}
              className="text-sm text-muted-foreground hover:text-foreground underline-offset-4"
            >
              שכחת סיסמה?
            </Button>
          </div>
        )}

        {mode === 'reset-verify' && (
          <div className="space-y-2">
            <Button
              variant="link"
              size="sm"
              type="button"
              onClick={async () => {
                if (resetCountdown === 0) {
                  await resetPassword(formData.email);
                }
              }}
              disabled={resetCountdown > 0 || loading}
              className="text-sm text-muted-foreground hover:text-foreground underline-offset-4 disabled:opacity-50"
            >
              {resetCountdown > 0
                ? `שלח שוב (${resetCountdown})`
                : 'לא קיבלת קוד? שלח שוב'
              }
            </Button>

          </div>
        )}

        {(mode === 'reset' || mode === 'reset-verify') && (
          <div>
            <Button
              variant="link"
              type="button"
              onClick={() => {
                setMode('login');
                setFormData({
                  email: '',
                  password: '',
                  confirmPassword: '',
                  firstName: '',
                  lastName: '',
                  verificationCode: '',
                  newPassword: '',
                  otpCode: '',
                });
              }}
              className="text-primary hover:text-primary/90 underline-offset-4 font-medium"
            >
              חזור להתחברות
            </Button>
          </div>
        )}
      </div>
    </form>
  </div>
);

const SuccessContent = () => (
  <div className="text-center py-8" dir="rtl">
    <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
      <Eye className="w-8 h-8 text-green-600" />
    </div>
    <h3 className="text-xl font-bold text-green-800 mb-2">חשבון נוצר בהצלחה!</h3>
    <p className="text-green-700">החשבון שלך נוצר ואומת בהצלחה</p>
    <div className="flex justify-center mt-4">
      <div className="w-6 h-6 border-2 border-green-600 border-t-transparent rounded-full animate-spin" />
    </div>
  </div>
);

const AuthModal = ({ isOpen, onClose, initialMode = 'login' }: AuthModalProps) => {
  // Support for login, signup, password reset with email code, and OTP verification
  const [mode, setMode] = useState<'login' | 'signup' | 'reset' | 'reset-verify' | 'otp-verify'>(initialMode);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isSendingCode, setIsSendingCode] = useState(false);
  const [countdown, setCountdown] = useState(60);
  const [resetCountdown, setResetCountdown] = useState(0); // Timer for password reset resend
  const [verified, setVerified] = useState(false);
  const [showExitConfirmation, setShowExitConfirmation] = useState(false);

  const { signIn, resetPassword, resetPasswordWithCode } = useAuth();
  const { checkRateLimit, recordAttempt } = useRateLimit('login');
  const isMobile = useIsMobile();

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    verificationCode: '',
    newPassword: '',
    otpCode: '',
  });

  // Effect to update mode if initialMode changes when reopening
  useEffect(() => {
    if (isOpen) {
      setMode(initialMode);
    }
  }, [isOpen, initialMode]);

  // Auto-send OTP when entering OTP verification mode
  useEffect(() => {
    // Don't auto-send on mode change anymore - code is sent before mode switch
  }, [mode]);

  // Countdown timer for OTP resend
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (countdown > 0 && mode === 'otp-verify') {
      interval = setInterval(() => {
        setCountdown(countdown - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [countdown, mode]);

  // Countdown timer for password reset resend
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (resetCountdown > 0 && mode === 'reset-verify') {
      interval = setInterval(() => {
        setResetCountdown(resetCountdown - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [resetCountdown, mode]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const sendVerificationCode = async () => {
    setIsSendingCode(true);
    try {
      const { data, error } = await supabase.functions.invoke('send-verification-code', {
        body: {
          email: formData.email,
          type: 'send',
          action: 'signup' // Explicitly set action for signup verification
        }
      });

      if (data?.error) {
        toast.error("שגיאה בהרשמה", {
          description: data.error,
        });
        return;
      }

      if (error) {
        console.error('Error sending verification code:', error);
        toast.error("שגיאה בשליחת קוד", {
          description: error.message || "לא ניתן לשלוח קוד אימות. נסה שוב מאוחר יותר",
        });
        return;
      }

      setCountdown(60);
      toast.success("קוד נשלח בהצלחה", {
        description: `קוד אימות נשלח אל ${formData.email}`,
      });
    } catch (error) {
      console.error('Error sending verification code:', error);
      toast.error("שגיאה בשליחת קוד", {
        description: "אירעה שגיאה בשליחת קוד האימות",
      });
    } finally {
      setIsSendingCode(false);
    }
  };

  const verifyOTPAndCreateAccount = async () => {
    setLoading(true);
    try {
      const cleanCode = formData.otpCode.trim().replace(/\s/g, '');

      const { data, error } = await supabase.functions.invoke('send-verification-code', {
        body: {
          email: formData.email.trim(),
          code: cleanCode,
          password: formData.password,
          firstName: formData.firstName.trim(),
          lastName: formData.lastName.trim(),
          type: 'verify'
        }
      });

      if (error || !data.success) {
        toast.error("קוד שגוי", {
          description: "הקוד שהזנת אינו נכון או שפג תוקפו. בדוק ונסה שוב",
        });
        recordAttempt(false);
        return;
      }

      setVerified(true);
      toast.success("חשבון נוצר בהצלחה!", {
        description: "החשבון שלך נוצר ואומת. מתחבר...",
      });

      // Sign in the user
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: formData.email,
        password: formData.password
      });

      if (signInError) {
        console.error('Error signing in after verification:', signInError);
        toast.error("שגיאה בהתחברות", {
          description: "החשבון נוצר אך אירעה שגיאה בהתחברות",
        });
        return;
      }

      recordAttempt(true);

      // Wait a moment then close modal
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (error) {
      console.error('Error verifying code:', error);
      toast.error("שגיאה באימות", {
        description: "אירעה שגיאה באימות הקוד",
      });
      recordAttempt(false);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseAttempt = () => {
    // If in verification mode, show confirmation before closing
    if (mode === 'otp-verify') {
      setShowExitConfirmation(true);
    } else {
      handleClose();
    }
  };

  const handleClose = () => {
    // Reset modal state when closing
    setMode('login');
    setFormData({
      email: '',
      password: '',
      confirmPassword: '',
      firstName: '',
      lastName: '',
      verificationCode: '',
      newPassword: '',
      otpCode: '',
    });
    setShowPassword(false);
    setVerified(false);
    setCountdown(60);
    setResetCountdown(0);
    setShowExitConfirmation(false);
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!checkRateLimit()) {
      recordAttempt(false);
      return;
    }

    if (mode === 'signup') {
      if (formData.password !== formData.confirmPassword) {
        toast.error("הסיסמאות אינן תואמות", {
          description: "נא לוודא שהסיסמה ואימות הסיסמה זהים",
        });
        recordAttempt(false);
        return;
      }

      const passwordValidation = validatePassword(formData.password);
      if (!passwordValidation.isValid) {
        toast.error("סיסמה לא תקינה", {
          description: passwordValidation.message,
        });
        recordAttempt(false);
        return;
      }
    }

    setLoading(true);

    try {
      if (mode === 'login') {
        const result = await signIn(formData.email, formData.password);
        if (!result.error) {
          recordAttempt(true);
          onClose();
        } else {
          recordAttempt(false);
        }
      } else if (mode === 'signup') {
        const { data: checkData, error: checkError } = await supabase.functions.invoke('send-verification-code', {
          body: {
            email: formData.email,
            type: 'send',
            action: 'signup'
          }
        });

        if (checkData?.error || checkData?.code === 'USER_EXISTS') {
          if (checkData.code === 'USER_EXISTS' || checkData.error?.includes('משתמש') || checkData.error?.includes('already exists')) {
            toast.error("משתמש קיים", {
              description: "משתמש עם כתובת מייל זו כבר קיים. אנא התחבר",
            });
          } else {
            toast.error("שגיאה", {
              description: checkData.error || "אירעה שגיאה. אנא נסה שוב.",
            });
          }
          recordAttempt(false);
          return;
        }

        if (checkError && !checkData) {
          console.error('Edge function error:', checkError);
          const errorString = JSON.stringify(checkError);
          const isConflict =
            (checkError as any)?.status === 409 ||
            errorString.includes('409') ||
            errorString.toLowerCase().includes('conflict') ||
            errorString.includes('משתמש');

          if (isConflict) {
            toast.error("משתמש קיים", {
              description: "משתמש עם כתובת מייל זו כבר קיים. אנא התחבר",
            });
          } else {
            toast.error("שגיאה בשרת", {
              description: "אירעה שגיאה בשרת. אנא נסה שוב מאוחר יותר",
            });
          }
          recordAttempt(false);
          return;
        }

        if (checkData?.error) {
          toast.error("שגיאה בהרשמה", {
            description: checkData.error,
          });
          recordAttempt(false);
          return;
        }

        if (checkData?.success) {
          recordAttempt(true);
          setCountdown(60);
          toast.success("קוד נשלח בהצלחה", {
            description: `קוד אימות נשלח אל ${formData.email}`,
          });
          setMode('otp-verify');
          return;
        }

        console.error('Unexpected signup response:', { checkData, checkError });
        toast.error("שגיאה בלתי צפויה", {
          description: "אנא נסה שוב",
        });
        recordAttempt(false);
        return;
      } else if (mode === 'otp-verify') {
        await verifyOTPAndCreateAccount();
      } else if (mode === 'reset') {
        await resetPassword(formData.email);
        recordAttempt(true);
        setResetCountdown(60);
        setMode('reset-verify');
      } else if (mode === 'reset-verify') {
        const result = await resetPasswordWithCode(formData.email, formData.verificationCode, formData.newPassword);
        if (!result.error) {
          recordAttempt(true);
          setMode('login');
          setFormData({
            email: '',
            password: '',
            confirmPassword: '',
            firstName: '',
            lastName: '',
            verificationCode: '',
            newPassword: '',
            otpCode: '',
          });
          setTimeout(() => {
            onClose();
          }, 500);
        } else {
          recordAttempt(false);
        }
      }
    } catch (error) {
      console.error('Auth error:', error);
      recordAttempt(false);
    } finally {
      setLoading(false);
    }
  };

  const getTitle = (): string => {
    switch (mode) {
      case 'login': return 'התחברות/הרשמה';
      case 'signup': return 'התחברות/הרשמה';
      case 'otp-verify': return 'אימות כתובת אימייל';
      case 'reset': return 'איפוס סיסמה';
      case 'reset-verify': return 'הזן קוד אימות';
      default: return 'התחברות';
    }
  };

  const getDescription = (): string => {
    switch (mode) {
      case 'login': return 'התחבר לחשבון שלך';
      case 'signup': return 'צור חשבון חדש ב-Excess Market';
      case 'otp-verify': return `קוד אימות נשלח אל ${formData.email}`;
      case 'reset': return 'הכנס את כתובת המייל שלך לקבלת קוד איפוס';
      case 'reset-verify': return 'הזן את קוד האימות שנשלח למייל והסיסמה החדשה';
      default: return '';
    }
  };




  // Show success state when verified
  if (verified) {
    return (
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent
          className="max-w-md w-[95vw] sm:w-full max-h-[90vh] overflow-y-auto"
          dir="rtl"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <SuccessContent />
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleCloseAttempt()}>
      <DialogContent
        className="max-w-md w-[95vw] sm:w-full max-h-[95vh] flex flex-col p-0 overflow-hidden"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <div className="overflow-y-auto p-6 md:p-8">
          <AuthContent
            isDrawer={false}
            mode={mode}
            setMode={setMode}
            formData={formData}
            setFormData={setFormData}
            loading={loading}
            isSendingCode={isSendingCode}
            countdown={countdown}
            resetCountdown={resetCountdown}
            showPassword={showPassword}
            setShowPassword={setShowPassword}
            handleSubmit={handleSubmit}
            getTitle={getTitle}
            getDescription={getDescription}
            handleInputChange={handleInputChange}
            sendVerificationCode={sendVerificationCode}
            onClose={onClose}
            resetPassword={resetPassword}
          />
        </div>
        <ConfirmationDialog
          isOpen={showExitConfirmation}
          onOpenChange={setShowExitConfirmation}
          onConfirm={handleClose}
        />
      </DialogContent>
    </Dialog>
  );
};

export default AuthModal;